import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../authConfig';

const API_BASE = process.env.REACT_APP_API_URL || '';

function Admin({ onClose, embedded }) {
    const [promotions, setPromotions] = useState([]);
    const [items, setItems] = useState([]);
    const [promoTypes, setPromoTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPromo, setEditingPromo] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importMode, setImportMode] = useState('merge');
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        startDate: '',
        endDate: '',
        details: '',
        promoType: '',
        items: []
    });

    // New item being added to promo
    const [newItem, setNewItem] = useState({ itemName: '', discount: '' });

    useEffect(() => {
        fetchData();
    }, []);

    async function fetchData() {
        try {
            const [promosRes, itemsRes, typesRes] = await Promise.all([
                authFetch(`${API_BASE}/api/promotions`),
                authFetch(`${API_BASE}/api/items`),
                authFetch(`${API_BASE}/api/promo-types`)
            ]);

            setPromotions(await promosRes.json());
            setItems(await itemsRes.json());
            setPromoTypes(await typesRes.json());
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
        }
    }

    function resetForm() {
        setFormData({
            name: '',
            startDate: '',
            endDate: '',
            details: '',
            promoType: '',
            items: []
        });
        setNewItem({ itemName: '', discount: '' });
    }

    function handleEdit(promo) {
        setEditingPromo(promo);
        setFormData({
            name: promo.name,
            startDate: promo.startDate,
            endDate: promo.endDate,
            details: promo.details || '',
            promoType: promo.promoType || '',
            items: promo.items.map(i => ({
                itemName: i.item,
                discount: i.discount ? (i.discount * 100).toString() : ''
            }))
        });
        setShowAddForm(true);
    }

    function handleAddItem() {
        if (!newItem.itemName) return;

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                itemName: newItem.itemName,
                discount: newItem.discount
            }]
        }));
        setNewItem({ itemName: '', discount: '' });
    }

    function handleRemoveItem(index) {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    }

    async function handleSubmit(e) {
        e.preventDefault();

        const payload = {
            name: formData.name,
            startDate: formData.startDate,
            endDate: formData.endDate,
            details: formData.details,
            promoType: formData.promoType,
            items: formData.items.map(i => ({
                itemName: i.itemName,
                discount: i.discount ? parseFloat(i.discount) / 100 : null
            }))
        };

        try {
            const url = editingPromo
                ? `${API_BASE}/api/promotions/${editingPromo.id}`
                : `${API_BASE}/api/promotions`;

            const method = editingPromo ? 'PUT' : 'POST';

            const res = await authFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert(editingPromo ? 'Promotion updated!' : 'Promotion added!');
                resetForm();
                setShowAddForm(false);
                setEditingPromo(null);
                fetchData();
            } else {
                const error = await res.json();
                alert('Error: ' + error.error);
            }
        } catch (error) {
            alert('Error saving promotion: ' + error.message);
        }
    }

    async function handleDelete(promoId) {
        if (!window.confirm('Are you sure you want to delete this promotion?')) {
            return;
        }

        try {
            const res = await authFetch(`${API_BASE}/api/promotions/${promoId}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                alert('Promotion deleted!');
                fetchData();
            } else {
                const error = await res.json();
                alert('Error: ' + error.error);
            }
        } catch (error) {
            alert('Error deleting promotion: ' + error.message);
        }
    }

    // Export promotions to CSV
    async function handleExport() {
        try {
            const res = await authFetch(`${API_BASE}/api/export/promotions`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
                alert('Export failed: ' + (err.error || res.statusText));
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Promo Details Export.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Export failed: ' + err.message);
        }
    }

    // Handle file selection for import
    function handleFileSelect(e) {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvData = event.target.result;
            await performImport(csvData);
        };
        reader.readAsText(file);
    }

    // Perform the CSV import
    async function performImport(csvData) {
        setImporting(true);
        try {
            const res = await authFetch(`${API_BASE}/api/import/promotions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ csvData, mode: importMode })
            });

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const text = await res.text();
                throw new Error(text || `Server error (${res.status})`);
            }

            const result = await res.json();

            if (res.ok) {
                alert(`Import successful!\n${result.message}`);
                setShowImportModal(false);
                fetchData();
            } else {
                alert('Import error: ' + result.error);
            }
        } catch (error) {
            alert('Import failed: ' + error.message);
        } finally {
            setImporting(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }

    if (loading) {
        return <div className="loading">Loading admin data...</div>;
    }

    const content = (
        <>
            <div className="admin-content">
                {!showAddForm ? (
                    <>
                        <div className="admin-toolbar">
                            <button onClick={() => { resetForm(); setShowAddForm(true); }}>
                                + Add New Promotion
                            </button>
                            <button className="secondary" onClick={() => setShowImportModal(true)}>
                                Import CSV
                            </button>
                            <button className="secondary" onClick={handleExport}>
                                Export CSV
                            </button>
                        </div>

                        <div className="admin-list">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Type</th>
                                        <th>Items</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promotions.map(promo => (
                                        <tr key={promo.id}>
                                            <td>{promo.name}</td>
                                            <td>{promo.startDate}</td>
                                            <td>{promo.endDate}</td>
                                            <td>{promo.promoType}</td>
                                            <td>{promo.items.length}</td>
                                            <td>
                                                <button
                                                    className="small"
                                                    onClick={() => handleEdit(promo)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="small danger"
                                                    onClick={() => handleDelete(promo.id)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSubmit} className="admin-form">
                        <h3>{editingPromo ? 'Edit Promotion' : 'Add New Promotion'}</h3>

                        <div className="form-group">
                            <label>Promotion Name *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Start Date *</label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>End Date *</label>
                                <input
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Promo Type</label>
                            <select
                                value={formData.promoType}
                                onChange={(e) => setFormData({ ...formData, promoType: e.target.value })}
                            >
                                <option value="">-- Select Type --</option>
                                {promoTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                                <option value="__new__">+ Add New Type</option>
                            </select>
                            {formData.promoType === '__new__' && (
                                <input
                                    type="text"
                                    placeholder="Enter new promo type"
                                    onChange={(e) => setFormData({ ...formData, promoType: e.target.value })}
                                    style={{ marginTop: '8px' }}
                                />
                            )}
                        </div>

                        <div className="form-group">
                            <label>Details</label>
                            <textarea
                                value={formData.details}
                                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                                rows={2}
                            />
                        </div>

                        <div className="form-group">
                            <label>Items Included</label>

                            <div className="item-add-row">
                                <select
                                    value={newItem.itemName}
                                    onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
                                >
                                    <option value="">-- Select Item --</option>
                                    <option value="All Webstore Products">All Webstore Products (Sitewide)</option>
                                    {items.map(item => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                                <input
                                    type="number"
                                    placeholder="Discount %"
                                    value={newItem.discount}
                                    onChange={(e) => setNewItem({ ...newItem, discount: e.target.value })}
                                    min="0"
                                    max="100"
                                    style={{ width: '100px' }}
                                />
                                <button type="button" onClick={handleAddItem}>Add</button>
                            </div>

                            <ul className="item-list">
                                {formData.items.map((item, idx) => (
                                    <li key={idx}>
                                        <span>{item.itemName}</span>
                                        {item.discount && <span className="discount">{item.discount}% off</span>}
                                        <button
                                            type="button"
                                            className="remove-btn"
                                            onClick={() => handleRemoveItem(idx)}
                                        >
                                            ×
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="form-actions">
                            <button type="submit">
                                {editingPromo ? 'Update Promotion' : 'Create Promotion'}
                            </button>
                            <button
                                type="button"
                                className="secondary"
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingPromo(null);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Import Modal */}
            {showImportModal && (
                <div className="import-modal-overlay">
                    <div className="import-modal">
                        <h3>Import Promotions from CSV</h3>

                        <div className="import-options">
                            <label>
                                <input
                                    type="radio"
                                    name="importMode"
                                    value="merge"
                                    checked={importMode === 'merge'}
                                    onChange={(e) => setImportMode(e.target.value)}
                                />
                                <strong>Merge</strong> - Add new promotions, update existing ones (matched by name + dates)
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="importMode"
                                    value="replace"
                                    checked={importMode === 'replace'}
                                    onChange={(e) => setImportMode(e.target.value)}
                                />
                                <strong>Replace All</strong> - Delete all existing promotions and import fresh
                            </label>
                        </div>

                        <div className="import-info">
                            <p><strong>CSV Format Required:</strong></p>
                            <p>Columns: Promotion Name, Start Date, End Date, Details, Promo Type, Item Included, Discount</p>
                            <p>Date format: M/D/YYYY (e.g., 1/15/2024)</p>
                            <p>Discount format: 20% or just 20</p>
                        </div>

                        <div className="import-actions">
                            <input
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={importing}
                            >
                                {importing ? 'Importing...' : 'Select CSV File'}
                            </button>
                            <button
                                className="secondary"
                                onClick={() => setShowImportModal(false)}
                                disabled={importing}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    const adminStyles = `
        .admin-content {
            padding: 20px;
            overflow-y: auto;
            flex: 1;
            color: var(--text-primary);
        }
        .admin-toolbar {
            margin-bottom: 20px;
            display: flex;
            gap: 10px;
        }
        .admin-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
        }
        .admin-table th,
        .admin-table td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid var(--border-primary);
            color: var(--text-primary);
        }
        .admin-table th {
            background: var(--table-header-bg);
            font-weight: 600;
        }
        .admin-table button.small {
            padding: 4px 8px;
            font-size: 12px;
            margin-right: 4px;
        }
        .admin-table button.danger {
            background: var(--red);
        }
        .admin-form {
            max-width: 600px;
        }
        .admin-form h3 {
            margin-bottom: 20px;
            color: var(--text-primary);
        }
        .form-group {
            margin-bottom: 16px;
        }
        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 600;
            font-size: 14px;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--border-input);
            border-radius: 4px;
            font-size: 14px;
            background: var(--bg-input);
            color: var(--text-primary);
        }
        .form-row {
            display: flex;
            gap: 16px;
        }
        .form-row .form-group {
            flex: 1;
        }
        .item-add-row {
            display: flex;
            gap: 8px;
            margin-bottom: 12px;
        }
        .item-add-row select {
            flex: 1;
        }
        .item-list {
            list-style: none;
            padding: 0;
            margin: 0;
            max-height: 200px;
            overflow-y: auto;
            border: 1px solid var(--border-primary);
            border-radius: 4px;
        }
        .item-list li {
            display: flex;
            align-items: center;
            padding: 8px 12px;
            border-bottom: 1px solid var(--border-light);
            gap: 12px;
            color: var(--text-primary);
        }
        .item-list li:last-child {
            border-bottom: none;
        }
        .item-list li span:first-child {
            flex: 1;
        }
        .item-list .discount {
            color: var(--red);
            font-weight: 600;
        }
        .item-list .remove-btn {
            background: transparent;
            color: var(--red);
            border: none;
            font-size: 18px;
            padding: 0 4px;
            cursor: pointer;
        }
        .form-actions {
            display: flex;
            gap: 12px;
            margin-top: 24px;
        }
        .import-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: var(--overlay-bg);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10002;
        }
        .import-modal {
            background: var(--bg-surface);
            border-radius: 8px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            box-shadow: var(--shadow-lg);
            color: var(--text-primary);
        }
        .import-modal h3 {
            margin: 0 0 20px 0;
            color: var(--text-primary);
        }
        .import-options {
            margin-bottom: 20px;
        }
        .import-options label {
            display: block;
            padding: 12px;
            border: 1px solid var(--border-input);
            border-radius: 4px;
            margin-bottom: 8px;
            cursor: pointer;
            color: var(--text-primary);
        }
        .import-options label:hover {
            background: var(--bg-surface-tertiary);
        }
        .import-options input[type="radio"] {
            margin-right: 8px;
        }
        .import-info {
            background: var(--bg-surface-tertiary);
            padding: 12px;
            border-radius: 4px;
            margin-bottom: 20px;
            font-size: 13px;
            color: var(--text-secondary);
        }
        .import-info p {
            margin: 4px 0;
        }
        .import-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
        }
    `;

    if (embedded) {
        return <>{content}<style>{adminStyles}</style></>;
    }

    return (
        <div className="admin-overlay">
            <div className="admin-container">
                <div className="admin-header">
                    <h2>Promotion Admin</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                {content}
            </div>
            <style>{`
                .admin-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: var(--overlay-bg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                }
                .admin-container {
                    background: var(--bg-surface);
                    border-radius: 8px;
                    width: 90%;
                    max-width: 1000px;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: var(--shadow-lg);
                }
                .admin-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    background: var(--dark-blue);
                    color: white;
                    border-radius: 8px 8px 0 0;
                }
                .admin-header h2 {
                    margin: 0;
                    font-size: 20px;
                }
                ${adminStyles}
            `}</style>
        </div>
    );
}

export default Admin;
